import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { notificationsApi, type NotificationView } from "../api/notifications";

const POLL_MS = 30_000;

/**
 * Bell icon with unread badge in the topbar. Polls /notifications/unread-count
 * on a 30 s cadence (cheap COUNT query). Opening the dropdown lazily fetches
 * the recent feed and marks individual rows read on click; "Mark all read"
 * empties unread state without reloading the feed.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationView[] | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const refreshCount = useCallback(() => {
    // Skip background ticks — a hidden tab doesn't need a fresh badge and
    // shouldn't hit the API (or spam 401s while a session is expiring).
    if (document.hidden) return;
    notificationsApi
      .unreadCount()
      .then((r) => setUnread(r.count))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    // Catch up immediately when the tab becomes visible again.
    document.addEventListener("visibilitychange", refreshCount);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", refreshCount);
    };
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && items === null) {
      notificationsApi
        .list()
        .then(setItems)
        .catch(() => setItems([]));
    }
  }

  function go(n: NotificationView) {
    if (!n.read) {
      notificationsApi.markRead(n.id).catch(() => {});
      setItems((prev) =>
        prev ? prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)) : prev,
      );
      setUnread((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.href) navigate(n.href);
  }

  function markAll() {
    notificationsApi
      .markAllRead()
      .then(() => {
        setItems((prev) => (prev ? prev.map((x) => ({ ...x, read: true })) : prev));
        setUnread(0);
      })
      .catch(() => {});
  }

  return (
    <div ref={containerRef} style={wrap}>
      <button
        type="button"
        onClick={toggle}
        className="topbar-icon-btn"
        aria-label={t("notifications.label", { defaultValue: "Obaveštenja" })}
      >
        <BellIcon />
        {unread > 0 && <span style={badge}>{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="dd-scroll notif-panel" style={dropdown}>
          <div style={head}>
            <strong style={{ fontSize: 13, color: "var(--brand-primary)" }}>
              {t("notifications.title", { defaultValue: "Obaveštenja" })}
            </strong>
            {unread > 0 && (
              <button type="button" onClick={markAll} style={linkBtn}>
                {t("notifications.markAllRead", { defaultValue: "Označi sve kao pročitano" })}
              </button>
            )}
          </div>
          {items === null && <div style={empty}>{t("common.loading", { defaultValue: "Učitavanje…" })}</div>}
          {items && items.length === 0 && (
            <div style={empty}>{t("notifications.empty", { defaultValue: "Nema obaveštenja" })}</div>
          )}
          {items && items.length > 0 && (
            <ul style={list}>
              {items.map((n) => (
                <li key={n.id}>
                  <button type="button" className={`notif-item${n.read ? "" : " is-unread"}`} onClick={() => go(n)}>
                    <span style={{ fontSize: 13, color: "var(--brand-primary)", fontWeight: n.read ? 400 : 600 }}>
                      {t(n.titleKey, { ...(n.placeholders ?? {}), defaultValue: n.titleKey })}
                    </span>
                    {n.bodyKey && (
                      <span style={{ display: "block", color: "var(--ink-2)", fontSize: 12, marginTop: 2 }}>
                        {t(n.bodyKey, { ...(n.placeholders ?? {}), defaultValue: n.bodyKey })}
                      </span>
                    )}
                    <span style={time}>{formatTime(n.createdAt, i18n.resolvedLanguage ?? "sr")}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  );
}

const wrap: React.CSSProperties = { position: "relative" };
const badge: React.CSSProperties = {
  position: "absolute",
  top: -4,
  right: -4,
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 8,
  background: "var(--err)",
  color: "#fff",
  fontSize: 10,
  lineHeight: "16px",
  textAlign: "center",
  fontWeight: 700,
};
const dropdown: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  right: 0,
  width: 360,
  maxHeight: 480,
  overflowY: "auto",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
  zIndex: 60,
};
const head: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 12px",
  borderBottom: "1px solid var(--surface-2)",
};
const linkBtn: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--brand-primary)",
  cursor: "pointer",
  fontSize: 12,
  textDecoration: "underline",
};
const empty: React.CSSProperties = { padding: 16, color: "var(--ink-3)", fontSize: 13 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0 };
const time: React.CSSProperties = {
  display: "block",
  marginTop: 4,
  color: "var(--ink-4)",
  fontSize: 11,
};
